import React, { useEffect, useMemo, useCallback } from 'react';
import { Form, Col, Button } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import useSelectProyecto from '../../../hooks/useMultiSelectProyectos';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import '../Finanzas/FormularioFiltroReporte/FormularioFiltroReporte.scss';

const SELECT_ALL = { id_proyecto: 0, nombre_proyecto: 'Seleccionar Todos ...' };

const toDateStr = d => {
  if (!d) return '';
  const x = d instanceof Date ? d : new Date(d);
  return x.toISOString().slice(0, 10);
};

const hoyStr = toDateStr(new Date());

const esActivo = p =>
  p.id_proyecto !== 0 &&
  p.fecha_inicio_proyecto <= hoyStr &&
  p.fecha_fin_proyecto    >= hoyStr;

const formatOptionLabel = p => (
  <span>
    <span style={{
      display: 'inline-block',
      width: 8, height: 8,
      borderRadius: '50%',
      background: p.id_proyecto === 0 ? 'transparent' : esActivo(p) ? '#22c55e' : '#d1d5db',
      marginRight: 6,
      verticalAlign: 'middle',
      flexShrink: 0,
    }} />
    {p.nombre_proyecto}
  </span>
);

const FormularioFiltroReporteNaranja = ({ datosIniciales, onSubmit }) => {
  const todosProyectos = useSelector(state => state.cursos.proyectos);
  const [seleccionProyecto, SelectProyecto, setSeleccionProyecto] = useSelectProyecto(null);

  const formik = useFormik({
    initialValues: {
      proyectoSeleccionado: datosIniciales.proyectoSeleccionado || [],
      fechaInicio: datosIniciales.fechaInicio || new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      fechaFin:    datosIniciales.fechaFin    || new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1),
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      fechaInicio: yup.date().typeError('Fecha inválida').required('Requerido'),
      fechaFin:    yup.date().typeError('Fecha inválida').required('Requerido'),
    }),
    onSubmit: valores => {
      const proyectos = (seleccionProyecto && seleccionProyecto.length > 0)
        ? seleccionProyecto
        : (datosIniciales.proyectoSeleccionado || []);
      onSubmit({ ...valores, proyectoSeleccionado: proyectos });
    },
  });

  // Solo muestra proyectos cuyo período se solapa con el rango de fechas seleccionado
  // y que ya hayan iniciado (no cursos futuros)
  const proyectosFiltrados = useMemo(() => {
    if (!todosProyectos || !todosProyectos.length) return [];
    const inicioStr = toDateStr(formik.values.fechaInicio);
    const finStr    = toDateStr(formik.values.fechaFin);
    const hoy       = toDateStr(new Date());
    if (!inicioStr || !finStr) return todosProyectos;
    return todosProyectos.filter(p =>
      p.fecha_inicio_proyecto <= finStr &&
      p.fecha_fin_proyecto    >= inicioStr &&
      p.fecha_inicio_proyecto <= hoy
    );
  }, [todosProyectos, formik.values.fechaInicio, formik.values.fechaFin]);

  // Cuando cambia el rango de fechas → auto-seleccionar todos los proyectos del período
  useEffect(() => {
    if (!proyectosFiltrados.length) return;
    setSeleccionProyecto(proyectosFiltrados);
  }, [proyectosFiltrados]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-submit cuando cambia la selección de proyectos
  useEffect(() => {
    if (!seleccionProyecto || seleccionProyecto.length === 0) return;
    formik.setFieldValue('proyectoSeleccionado', seleccionProyecto);
    onSubmit({ ...formik.values, proyectoSeleccionado: seleccionProyecto });
  }, [seleccionProyecto]); // eslint-disable-line react-hooks/exhaustive-deps

  // onChange personalizado: "Seleccionar Todos" usa solo los proyectos del período
  const handleChangeProyecto = useCallback((selected) => {
    if (!selected) { setSeleccionProyecto([]); return; }
    const hasAll = selected.find(p => p.id_proyecto === 0);
    setSeleccionProyecto(hasAll ? proyectosFiltrados : selected);
  }, [proyectosFiltrados, setSeleccionProyecto]);

  const opciones = [SELECT_ALL, ...proyectosFiltrados];

  return (
    <div className="FormularioFiltroReporte">
      <Form onSubmit={formik.handleSubmit}>
        <Form.Row>
          <Form.Group as={Col} md={6}>
            <Form.Label>Producto (Calidad / Fidelización)</Form.Label>
            <SelectProyecto
              value={seleccionProyecto}
              options={opciones}
              onChange={handleChangeProyecto}
              formatOptionLabel={formatOptionLabel}
            />
          </Form.Group>

          <Form.Group as={Col} md={2}>
            <Form.Label className="w-100">Recaudación: Desde</Form.Label>
            <DatePicker
              selected={formik.values.fechaInicio}
              onChange={date => formik.setFieldValue('fechaInicio', date)}
              selectsStart
              dateFormat="dd/MM/yyyy"
              name="fechaInicio"
              startDate={formik.values.fechaInicio}
              className="form-control"
              endDate={formik.values.fechaFin}
            />
            {formik.errors.fechaInicio && (
              <Form.Text className="text-danger">{formik.errors.fechaInicio}</Form.Text>
            )}
          </Form.Group>

          <Form.Group as={Col} md={2}>
            <Form.Label className="w-100">Recaudación: Hasta</Form.Label>
            <DatePicker
              selected={formik.values.fechaFin}
              onChange={date => formik.setFieldValue('fechaFin', date)}
              className="form-control"
              dateFormat="dd/MM/yyyy"
              selectsEnd
              name="fechaFin"
              startDate={formik.values.fechaInicio}
              endDate={formik.values.fechaFin}
              minDate={formik.values.fechaInicio}
            />
            {formik.errors.fechaFin && (
              <Form.Text className="text-danger">{formik.errors.fechaFin}</Form.Text>
            )}
          </Form.Group>

          <Form.Group as={Col} md={2} className="d-flex align-items-end justify-content-end">
            <Button variant="primary" type="submit" className="btn-icon">
              <FontAwesomeIcon icon={faSearch} />
              Buscar
            </Button>
          </Form.Group>
        </Form.Row>
      </Form>
    </div>
  );
};

export default FormularioFiltroReporteNaranja;
